import { Router } from 'express';
import { db } from '../storage';
import { ZaloNotificationLog } from '../../src/types';

export const zaloRouter = Router();

// Get notification history for teacher
zaloRouter.get('/logs', (req, res) => {
  const teacherId = req.query.teacherId as string;
  if (!teacherId) return res.status(400).json({ error: 'Missing teacherId' });
  const logs = db.getZaloLogs(teacherId);
  res.json(logs);
});

// Send or trigger Zalo Notification
zaloRouter.post('/send', async (req, res) => {
  try {
    const { teacherId, recipientName, recipientPhone, type, content, sessionId } = req.body;
    if (!teacherId || !content) {
      return res.status(400).json({ error: 'Missing required fields' });
    }

    const user = db.getUserById(teacherId);
    if (!user) return res.status(404).json({ error: 'Teacher not found' });

    // Format clean phone for Zalo link (e.g. 0912345678 or +84912345678 -> 84912345678)
    let cleanPhone = (recipientPhone || '').replace(/\D/g, '');
    if (cleanPhone.startsWith('0')) {
      cleanPhone = '84' + cleanPhone.substring(1);
    }
    const zaloDirectLink = cleanPhone ? `https://zalo.me/${cleanPhone}` : '';

    let webhookStatus: 'sent' | 'pending' | 'failed' | 'direct_link_opened' = 'sent';

    // If teacher configured a custom Zalo Webhook / OA endpoint
    if (user.zaloWebhookUrl && user.zaloWebhookUrl.startsWith('http')) {
      try {
        await fetch(user.zaloWebhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            recipient: recipientPhone,
            name: recipientName,
            message: content,
            type,
            sentBy: user.name,
            timestamp: new Date().toISOString()
          })
        });
      } catch (e) {
        console.warn('Zalo Webhook error (using simulation fallback):', e);
      }
    }

    const log: ZaloNotificationLog = {
      id: `zalo_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      teacherId,
      type: type || 'session_reminder',
      recipientName: recipientName || 'Học sinh / Phụ huynh',
      recipientPhone: recipientPhone || user.phone || '0901234567',
      content,
      status: webhookStatus,
      sentAt: new Date().toISOString(),
    };

    db.addZaloLog(log);

    if (sessionId) {
      db.updateSession(teacherId, sessionId, {
        reminderSentZalo: true,
        lastZaloReminderAt: new Date().toISOString()
      });
    }

    res.json({
      success: true,
      log,
      zaloDirectLink,
      encodedMessageForZalo: encodeURIComponent(content),
      message: 'Đã tạo và gửi thông báo Zalo thành công!'
    });
  } catch (error: any) {
    res.status(500).json({ error: error.message || 'Lỗi khi gửi thông báo Zalo' });
  }
});

// Generate quick Zalo template messages
zaloRouter.post('/generate-template', (req, res) => {
  const { type, teacherName, className, date, time, location, studentName, parentName, feeAmount, topic } = req.body;

  let message = '';
  switch (type) {
    case 'session_reminder_parent':
      message = `[NHẮC LỊCH HỌC]\nKính gửi ${parentName || 'Quý Phụ huynh'},\nThầy/Cô ${teacherName || ''} xin nhắc lịch học lớp ${className || ''} của em ${studentName || ''}:\n- Thời gian: ${time || '17:30 - 19:30'}, ngày ${date || 'hôm nay'}\n- Địa điểm: ${location || 'Phòng học'}\n- Nội dung buổi học: ${topic || 'Bài mới và chữa bài tập'}\nKính mong Quý Phụ huynh nhắc nhở em chuẩn bị bài đầy đủ. Trân trọng cảm ơn!`;
      break;
    
    case 'session_reminder_teacher':
      message = `[LỊCH DẠY HÔM NAY]\nChào Thầy/Cô ${teacherName},\nLịch dạy tiếp theo của Thầy/Cô:\n- Lớp: ${className}\n- Giờ dạy: ${time} (${date})\n- Địa điểm: ${location}\n- Chủ đề: ${topic || 'Theo giáo án'}\nChúc Thầy/Cô có buổi dạy tràn đầy năng lượng!`;
      break;

    case 'tuition_notice':
      message = `[THÔNG BÁO HỌC PHÍ]\nKính gửi ${parentName || 'Quý Phụ huynh'},\nThầy/Cô ${teacherName || ''} xin gửi thông báo học phí tháng này của em ${studentName || ''} lớp ${className || ''}:\n- Số tiền cần đóng: ${(feeAmount || 0).toLocaleString('vi-VN')} VNĐ\n- Hình thức nộp: Tiền mặt tại lớp hoặc Chuyển khoản.\nQuý Phụ huynh vui lòng hoàn tất học phí sớm giúp Thầy/Cô. Xin chân thành cảm ơn!`;
      break;

    case 'attendance_report':
      message = `[BÁO CÁO BUỔI HỌC]\nKính gửi ${parentName || 'Quý Phụ huynh'},\nThầy/Cô ${teacherName || ''} thông báo tình hình học tập hôm nay của em ${studentName || ''} lớp ${className || ''}:\n- Điểm danh: Đã tham gia đầy đủ, đúng giờ.\n- Thái độ học tập: Chăm chú nghe giảng và làm bài tích cực.\n- Bài tập về nhà: Đã giao phiếu bài tập, hạn nộp vào buổi học kế tiếp.\nTrân trọng!`;
      break;

    case 'reschedule_notice':
      message = `[THÔNG BÁO DỜI LỊCH / DẠY BÙ]\nKính gửi ${parentName || 'Quý Phụ huynh'} và các em học sinh lớp ${className || ''},\nDo có việc đột xuất, buổi học ngày ${date || ''} sẽ được chuyển sang lịch học bù vào lúc: ${time || '19:00'} ngày ${location || 'Chủ Nhật tuần này'}.\nRất mong Quý Phụ huynh thông cảm và hỗ trợ sắp xếp cho các em!`;
      break;

    default:
      message = `Kính gửi Quý Phụ huynh và học sinh lớp ${className || ''},\nThầy/Cô ${teacherName || ''} xin gửi thông báo lịch học và tài liệu mới. Trân trọng!`;
  }

  res.json({ template: message });
});
